import { Link } from '@remix-run/react'
import { appName } from '#app/utils/constants.ts'
import { cn } from '#app/utils/tailwind-merge.js'

/**
 * The logo is drawn rather than shipped as artwork.
 *
 * As outlined vector art the tagline came out ~7px tall at header size, and
 * outlines get none of the grid-fitting the font engine applies to real text —
 * so on a 1x display its stems landed at fractional pixel widths and smeared.
 * Setting the two runs as live Inter hands them back to the text rasteriser.
 * Only the swoosh, which is a shape and not type, stays vector.
 *
 * Geometry is in the 505x67 units of the Figma export, so it stays checkable
 * against `public/img/logo.svg` — which is still what the OG card is rendered
 * from, that being a large raster where none of this applies.
 */

const SWOOSH = 'M0 7.28876L505 0L400.804 64L473.204 8.91389L0 7.28876Z'

const ARTBOARD = { w: 505, h: 67 }

/**
 * Distance from the top of a `line-height: 1` box down to the baseline, as a
 * fraction of font-size. Falls out of Inter's ascent/descent; measured rather
 * than derived, since which metrics the browser uses (hhea vs OS/2 typo)
 * varies by platform.
 */
const BASELINE_IN_LINE_BOX = 0.865

/**
 * Type spec from the Figma export, plus the placement solved from it.
 *
 * `opsz` is pinned per run rather than left to `font-optical-sizing: auto`.
 * Auto ties optical size to the rendered pixel size, so the wordmark would
 * change width as the header resizes; these two values are the ones whose ink
 * width matches the export (to within 0.03 of 505 units). Pinning them also
 * means the lockup is identical at every size.
 *
 * `originX` is the text origin, chosen so each run's ink is centred on the
 * export's ink centre. `baselineY` puts the cap line where the export has it.
 */
const RUNS = [
	{
		text: appName.toUpperCase(),
		size: 36,
		weight: 900,
		opsz: 19.4,
		originX: 39.466,
		baselineY: 40.002,
	},
	{
		text: 'Helping businesses create abundance'.toUpperCase(),
		size: 15,
		weight: 600,
		opsz: 16.2,
		originX: 71.992,
		baselineY: 57.001,
	},
]

const Logo = ({ className }: { className?: string }) => {
	return (
		<Link
			to="/"
			className={cn(
				'inline-block rounded-md text-[1.5rem] sm:text-[2rem]',
				className,
			)}
		>
			<span
				role="img"
				aria-label={`${appName} — Helping businesses create abundance`}
				className="relative block h-[1em] select-none"
				style={{ width: `${ARTBOARD.w / ARTBOARD.h}em` }}
			>
				<svg
					viewBox={`0 0 ${ARTBOARD.w} ${ARTBOARD.h}`}
					fill="currentColor"
					aria-hidden
					className="absolute inset-0 h-full w-full"
				>
					<path fillRule="evenodd" clipRule="evenodd" d={SWOOSH} />
				</svg>
				{RUNS.map(run => (
					<span
						key={run.text}
						className="absolute whitespace-nowrap"
						style={{
							left: `${(run.originX / ARTBOARD.w) * 100}%`,
							top: `${((run.baselineY - BASELINE_IN_LINE_BOX * run.size) / ARTBOARD.h) * 100}%`,
							fontSize: `${run.size / ARTBOARD.h}em`,
							fontFamily: `'Casal Logo', 'Inter Variable', sans-serif`,
							fontStyle: 'italic',
							fontWeight: run.weight,
							fontVariationSettings: `'opsz' ${run.opsz}, 'wght' ${run.weight}`,
							lineHeight: 1,
							letterSpacing: 0,
						}}
					>
						{run.text}
					</span>
				))}
			</span>
		</Link>
	)
}
export { Logo }
